import axios, { AxiosError, AxiosRequestConfig } from "axios";
import type {
  SlaComplianceLevel,
  SlaComplianceQuery,
  SlaComplianceResponse,
  SlaDefinitionDto,
  SlaDefinitionInput,
  SlaDefinitionsPutRequest,
  SlaDefinitionsPutResponse,
  SlaDefinitionsResponse,
  SlaDiagramResponse,
  SlaDrilldownResponse,
  SlaInstancesQuery,
  SlaInstancesResponse,
  SlaKpiResponse,
  SlaModelBreakdownResponse,
  SlaMutationResponse,
  SlaOverrideRequest,
  SlaPreviewRequest,
  SlaPreviewResponse,
  SlaRecalculateRequest,
  SlaSettingsDto,
  SlaSettingsInput,
  SlaTrendResponse,
  SlaUserPreferencesDto,
  SlaUserPreferencesInput,
} from "model/slaModel";
import { SLA_ERROR_CODES, SLA_INSTANCES_PAGE_SIZE } from "model/slaModel";
import type { SlaErrorCode } from "model/slaModel";
import { slaStatisticsDrilldownPath } from "model/slaStatisticsModel";
import errorInterceptor from "./errorInterceptor";
import jwtInterceptor from "./jwtInterceptor";
import {
  SLA_FIXTURE_COMPLIANCE,
  SLA_FIXTURE_DEFINITIONS,
  SLA_FIXTURE_DIAGRAM,
  SLA_FIXTURE_DRILLDOWN,
  SLA_FIXTURE_INSTANCES,
  SLA_FIXTURE_KPI,
  SLA_FIXTURE_MODEL_BREAKDOWN,
  SLA_FIXTURE_PREFERENCES,
  SLA_FIXTURE_PREVIEW_ESTIMABLE,
  SLA_FIXTURE_SETTINGS,
  SLA_FIXTURE_TREND,
} from "./slaFixtures";

/**
 * Client del contratto SLA (C1, piano Client sez. 5 e 11; Server sez. 10.4 e 11.1).
 *
 * Due backend, due istanze axios separate, senza toccare i moduli esistenti:
 * - il backend Delphi espone tutte le rotte `/sla/organizations/...` e
 *   `/sla/preferences` sul base URL principale (stesso pattern di `baseApi`);
 * - il backend Python espone le statistiche SLA (`/sla/{org}/compliance`, ...)
 *   sul base URL helper (stesso pattern di `statisticsApi`).
 * Le basi URL non sono dedotte a runtime: sono le env gia' usate dal progetto.
 *
 * Gli errori seguono la convenzione del repo: l'`errorInterceptor` mostra lo
 * snackbar e rilancia l'errore axios. Chi deve distinguere 409/422/403 usa
 * `toSlaApiError`, che ricava il codice stabile dallo status HTTP senza
 * interpretare il testo tradotto del backend.
 */

const instance = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_BASE_URI}sla`,
  headers: {
    "Content-Type": "application/json",
  },
});

instance.interceptors.request.use(jwtInterceptor);
instance.interceptors.response.use(undefined, errorInterceptor);

/**
 * Istanza del backend Python (helpers). Le rotte di trend, drill-down,
 * suggerimenti, insight ed export arrivano con F4: qui c'e' solo la compliance,
 * la cui forma e' congelata in C1 sez. 5.
 */
export const slaStatisticsApi = axios.create({
  baseURL: `${process.env.REACT_APP_STATS_BACKEND_BASE_URI}/sla`,
  headers: {
    "Content-Type": "application/json",
  },
});

slaStatisticsApi.interceptors.request.use(jwtInterceptor);
slaStatisticsApi.interceptors.response.use(undefined, errorInterceptor);

// ─── Fixture locali ──────────────────────────────────────────────────────────

/**
 * Sviluppo UI senza backend: le rotte `/sla/...` non sono ancora esposte in un
 * ambiente di test (piano Client sez. 11.2 e 11.5 punto 5). *(Rettifica
 * 2026-09-20: non perche' manchi il commit — sono committate dal `47816260` —
 * ma perche' il Server e' **non compilato** e la rotta non e' servita.)*
 * Con `REACT_APP_SLA_FIXTURES=true` le letture restituiscono le fixture di
 * `slaFixtures.ts` e le mutazioni rispondono come il backend (nessuna scrittura).
 */
const SLA_FIXTURES_ENABLED = process.env.REACT_APP_SLA_FIXTURES === "true";

export const isSlaFixtureMode = (): boolean => SLA_FIXTURES_ENABLED;

const FIXTURE_MUTATION_RESPONSE: SlaMutationResponse = {
  success: true,
  responsemessage: "sla.fixture.mutation",
};

/**
 * Nessun altro modulo del repo ha una modalita' fixture: l'avviso una volta sola
 * evita che dati simulati vengano scambiati per reali in demo o in test E2E.
 */
if (SLA_FIXTURES_ENABLED) {
  console.warn(
    "[slaApi] REACT_APP_SLA_FIXTURES=true: risposte simulate, nessuna chiamata reale alle API SLA.",
  );
}

const request = async <T>(
  config: AxiosRequestConfig,
  fixture: T,
): Promise<T> => SLA_FIXTURES_ENABLED ? fixture : (await instance.request<T>(config)).data;

const statisticsRequest = async <T>(
  config: AxiosRequestConfig,
  fixture: T,
): Promise<T> =>
  SLA_FIXTURES_ENABLED
    ? fixture
    : (await slaStatisticsApi.request<T>(config)).data;

// ─── Errori stabili ──────────────────────────────────────────────────────────

/**
 * Errore normalizzato. `code` e' derivato dallo status HTTP: il backend
 * restituisce il messaggio tradotto per la lingua del chiamante, non il codice
 * stabile, che resta quindi una convenzione di contratto per il client.
 */
export interface SlaApiError {
  status: number | null;
  code: SlaErrorCode | null;
  message: string;
  /** true sui 409: la revisione attesa e' divergente e la bozza non va persa. */
  revisionConflict: boolean;
  /**
   * Chiave `sla_*` del 400 (`detailedmessage` / `items[0]` dell'EMVCException):
   * il client la traduce dal catalogo. `null` se il corpo non la porta.
   */
  messageKey?: string | null;
}

type SlaErrorBody = {
  message?: string;
  responsemessage?: string;
  detail?: string;
  detailedmessage?: string;
  items?: unknown[];
};

const SLA_ERROR_KEY_PATTERN = /^sla_[a-z0-9_]+$/;

/**
 * Il codice preciso di un 404 dipende dalla rotta (`sla_model_not_found` per il
 * diagramma, `sla_instance_not_found` per l'override): il chiamante lo dichiara.
 */
export const toSlaApiError = (
  error: unknown,
  notFoundCode: SlaErrorCode = SLA_ERROR_CODES.instanceNotFound,
): SlaApiError => {
  const axiosError = error as AxiosError<SlaErrorBody>;
  const status = axiosError?.response?.status ?? null;
  const body = axiosError?.response?.data;

  const code = (() => {
    switch (status) {
      case 401:
        return SLA_ERROR_CODES.forbidden;
      case 403:
        return SLA_ERROR_CODES.adminRequired;
      case 404:
        return notFoundCode;
      case 409:
        return SLA_ERROR_CODES.revisionConflict;
      case 422:
        return SLA_ERROR_CODES.reasonRequired;
      default:
        return null;
    }
  })();

  return {
    status,
    code,
    message:
      body?.message ||
      body?.responsemessage ||
      body?.detail ||
      axiosError?.message ||
      "",
    revisionConflict: status === 409,
    messageKey:
      [body?.detailedmessage, body?.items?.[0]].find(
        (candidate): candidate is string =>
          typeof candidate === "string" && SLA_ERROR_KEY_PATTERN.test(candidate),
      ) ?? null,
  };
};

// ─── Definizioni modello/versione ────────────────────────────────────────────

/**
 * GET: le definizioni sono serializzate dal modello DMVC in minuscolo senza
 * underscore (`targettype`, `escalationafterworkinghours`); il PUT invece legge
 * chiavi snake_case. `slaDefinitionDtoToInput` fa il passaggio inverso senza
 * duplicare la mappatura nei componenti.
 */
export const fetchSlaDefinitions = async (
  idorganization: number,
  idprocessmodel: number,
  modelRevision?: number,
): Promise<SlaDefinitionsResponse> =>
  request(
    {
      method: "GET",
      url: `/organizations/${idorganization}/models/${idprocessmodel}/definitions`,
      params: modelRevision ? { model_revision: modelRevision } : undefined,
    },
    SLA_FIXTURE_DEFINITIONS,
  );

/**
 * PUT: 202 con gli avvisi di validazione SOFT. Un 409 (`expected_revision`
 * divergente) non scrive nulla e non consuma la bozza del chiamante.
 */
export const saveSlaDefinitions = async (
  idorganization: number,
  idprocessmodel: number,
  body: SlaDefinitionsPutRequest,
): Promise<SlaDefinitionsPutResponse> =>
  request(
    {
      method: "PUT",
      url: `/organizations/${idorganization}/models/${idprocessmodel}/definitions`,
      data: body,
    },
    { meta: { warnings: [] } },
  );

export const slaDefinitionDtoToInput = (
  dto: SlaDefinitionDto,
): SlaDefinitionInput => ({
  target_type: dto.targettype,
  target_designerid: dto.targetdesignerid,
  duration_format: dto.durationformat,
  duration_value: dto.durationvalue,
  duration_unit: dto.durationunit,
  risk_remaining_percent: dto.riskremainingpercent,
  responsible_user_id: dto.responsibleuserid,
  responsible_role_id: dto.responsibleroleid,
  escalation_after_working_hours: dto.escalationafterworkinghours,
  escalation_levels: dto.escalationlevels,
  escalation_role_id: dto.escalationroleid,
});

/**
 * I campi escalation sono nelle stesse tre colonne sui due lati del contratto,
 * ma il significato va letto con le regole della sez. 11.1 (ritardo NULL =
 * nessuna escalation, livelli 1..5, ruolo NULL = ricaduta sul responsabile).
 * Le regole vivono nel modello e sono ri-esportate qui perche' il Monitor le
 * consuma dal barrel `api` come gli altri adattatori del progetto.
 */
export {
  isSlaEscalationActive,
  isSlaEscalationLevel,
  isSlaEscalationRuntimeEnabled,
  slaEscalationFromDefinition,
} from "model/slaModel";
export type { SlaEscalationConfig } from "model/slaModel";

// ─── Preview ─────────────────────────────────────────────────────────────────

/** Nessuna creazione: `estimable = false` e' un avviso, non un errore. */
export const previewSlaDefinition = async (
  idorganization: number,
  body: SlaPreviewRequest,
): Promise<SlaPreviewResponse> =>
  request(
    { method: "POST", url: `/organizations/${idorganization}/preview`, data: body },
    SLA_FIXTURE_PREVIEW_ESTIMABLE,
  );

// ─── Monitor ─────────────────────────────────────────────────────────────────

/**
 * Istanze aperte, stato calcolato all'istante della query (`as_of`).
 * `page` e' 0-based: il Server calcola l'offset come `page * pagesize`
 * (Server sez. 10.4) e il meta del Server e' coerente con questa lettura.
 */
export const fetchSlaInstances = async (
  idorganization: number,
  query: SlaInstancesQuery = {},
): Promise<SlaInstancesResponse> =>
  request(
    {
      method: "GET",
      url: `/organizations/${idorganization}/instances`,
      params: { pagesize: SLA_INSTANCES_PAGE_SIZE, ...query },
    },
    SLA_FIXTURE_INSTANCES,
  );

/** Conteggi per livello, per il Monitor: nessuna metrica per persona. */
export const fetchSlaKpi = async (
  idorganization: number,
  level: number = 0,
): Promise<SlaKpiResponse> =>
  request(
    {
      method: "GET",
      url: `/organizations/${idorganization}/kpi`,
      params: level > 0 ? { level } : undefined,
    },
    SLA_FIXTURE_KPI,
  );

/**
 * Breakdown per nodo: i nodi senza SLA sono inclusi. 404 `sla_model_not_found`
 * per modello inesistente, cancellato, di altro tenant o revisione non
 * risolvibile; `meta.model_revision` e' una stringa.
 */
export const fetchSlaDiagram = async (
  idorganization: number,
  idprocessmodel: number,
  modelRevision?: number,
): Promise<SlaDiagramResponse> =>
  request(
    {
      method: "GET",
      url: `/organizations/${idorganization}/models/${idprocessmodel}/diagram`,
      params: modelRevision ? { model_revision: modelRevision } : undefined,
    },
    SLA_FIXTURE_DIAGRAM,
  );

/**
 * Codice da passare a `toSlaApiError` quando fallisce il diagramma: il 404 di
 * quella rotta e' `sla_model_not_found`, non `sla_instance_not_found`.
 */
export const SLA_DIAGRAM_NOT_FOUND_CODE = SLA_ERROR_CODES.modelNotFound;

// ─── Override e ricalcolo (admin-only) ───────────────────────────────────────

/**
 * PATCH override: `expected_revision` e `reason` obbligatori. 403 non admin,
 * 404 istanza di altro tenant, 409 revisione divergente, 422 motivo mancante.
 * La rotta implementata accetta la sola deadline assoluta (punto aperto F1).
 */
export const overrideSlaInstance = async (
  idorganization: number,
  id: number,
  body: SlaOverrideRequest,
): Promise<SlaMutationResponse> =>
  request(
    {
      method: "PATCH",
      url: `/organizations/${idorganization}/instances/${id}`,
      data: body,
    },
    FIXTURE_MUTATION_RESPONSE,
  );

/** Ricalcolo calendario esplicito: la deadline originale non viene riscritta. */
export const recalculateSlaInstance = async (
  idorganization: number,
  id: number,
  body: SlaRecalculateRequest,
): Promise<SlaMutationResponse> =>
  request(
    {
      method: "POST",
      url: `/organizations/${idorganization}/instances/${id}/recalculate`,
      data: body,
    },
    FIXTURE_MUTATION_RESPONSE,
  );

// ─── Impostazioni di organizzazione (admin-only) ─────────────────────────────

/**
 * GET: chiavi minuscole senza underscore (modello DMVC). Senza riga il Server
 * espone i default dello schema, senza scrivere nulla.
 */
export const fetchSlaSettings = async (
  idorganization: number,
): Promise<SlaSettingsDto> =>
  request(
    { method: "GET", url: `/organizations/${idorganization}/settings` },
    SLA_FIXTURE_SETTINGS,
  );

/** PUT: chiavi snake_case; i campi assenti non sono modificati. */
export const saveSlaSettings = async (
  idorganization: number,
  body: SlaSettingsInput,
): Promise<SlaMutationResponse> =>
  request(
    {
      method: "PUT",
      url: `/organizations/${idorganization}/settings`,
      data: body,
    },
    FIXTURE_MUTATION_RESPONSE,
  );

// ─── Preferenze utente ───────────────────────────────────────────────────────

/**
 * Le preferenze sono per (utente, organizzazione): l'organizzazione e' un
 * parametro esplicito, non e' deducibile (un utente puo' stare in piu'
 * organizzazioni). Non richiedono admin.
 */
export const fetchSlaPreferences = async (
  idorganization: number,
): Promise<SlaUserPreferencesDto> =>
  request(
    {
      method: "GET",
      url: "/preferences",
      params: { idorganization },
    },
    SLA_FIXTURE_PREFERENCES,
  );

/**
 * PATCH: preferenze dell'utente autenticato. Il saluto si chiude una volta sola.
 * L'organizzazione va indicata: il controller la cerca prima nella query string
 * e, solo se assente, nel body; senza di essa risponde 422. Il client la manda in
 * entrambi i posti per non dipendere da quale delle due strade restera' stabile.
 */
export const patchSlaPreferences = async (
  idorganization: number,
  body: SlaUserPreferencesInput,
): Promise<SlaMutationResponse> =>
  request(
    {
      method: "PATCH",
      url: "/preferences",
      params: { idorganization },
      data: { ...body, idorganization },
    },
    FIXTURE_MUTATION_RESPONSE,
  );

// ─── Statistiche SLA (backend Python) ────────────────────────────────────────

/**
 * Conformita' (P2): `from`/`to` sono date del periodo `[from, to)` e `level`
 * (`process`/`task`) e' **obbligatorio** — il contratto non espone un aggregato
 * dei due livelli. `modelids`/`teamids`/`customervalues` sono liste separate da
 * virgola; F4a invia solo `from`/`to`/`level`/`modelids` (Cliente e team sono
 * F4b). La rotta e' `GET /sla/{org}/compliance` sul backend Python, coerente con
 * la base URL di `slaStatisticsApi`.
 */
export const fetchSlaCompliance = async (
  idorganization: number,
  query: SlaComplianceQuery,
): Promise<SlaComplianceResponse> =>
  statisticsRequest(
    { method: "GET", url: `/${idorganization}/compliance`, params: query },
    SLA_FIXTURE_COMPLIANCE,
  );

/**
 * **Trend** (F4b): `GET /sla/{org}/trend` (`controllers/sla.py:190-222`). Stessa
 * forma della conformita' per periodo/livello/modello, piu' `granularity`.
 */
export const fetchSlaTrend = async (
  idorganization: number,
  query: SlaComplianceQuery,
): Promise<SlaTrendResponse> =>
  statisticsRequest(
    { method: "GET", url: `/${idorganization}/trend`, params: query },
    SLA_FIXTURE_TREND,
  );

/**
 * **Drill-down** (F4b): `GET /sla/{org}/drilldown/process|step`
 * (`controllers/sla.py:225-278`). Il livello sta **nel path**, non nella query:
 * il path lo decide `slaStatisticsDrilldownPath` (`slaStatisticsModel.ts`), non
 * il chiamante, cosi' il livello reso e quello richiesto non possono divergere.
 */
export const fetchSlaDrilldown = async (
  idorganization: number,
  level: SlaComplianceLevel,
  query: SlaComplianceQuery,
): Promise<SlaDrilldownResponse> =>
  statisticsRequest(
    {
      method: "GET",
      url: `/${idorganization}/${slaStatisticsDrilldownPath(level)}`,
      params: query,
    },
    SLA_FIXTURE_DRILLDOWN,
  );

/**
 * **Ripartizione per modello**: `GET /sla/{org}/breakdown/model` sul backend
 * Python. Stessa query della conformita' (`from`/`to`/`level`/`modelids`/...):
 * i gruppi sommano esattamente le schede KPI.
 */
export const fetchSlaModelBreakdown = async (
  idorganization: number,
  query: SlaComplianceQuery,
): Promise<SlaModelBreakdownResponse> =>
  statisticsRequest(
    { method: "GET", url: `/${idorganization}/breakdown/model`, params: query },
    SLA_FIXTURE_MODEL_BREAKDOWN,
  );

export default instance;
